/**
 * ============================================
 * LEVEL SETTINGS CONTEXT
 * ============================================
 *
 * Global, S3-backed level categorization shared by every Overseer client.
 *
 * Categories (5): Single Player, Labz, UGC, 2D, PVP.
 * Anything not explicitly assigned resolves to UGC by default (the product
 * default for the 3000+ uncategorized levels) -- except levels that match the
 * built-in Single Player / Labz name patterns, which keep their auto-grouping.
 *
 * Storage: the categorization lives in a backend manifest persisted to S3
 * (api/v1/level-settings). An admin edits it behind an exclusive lock acquired
 * when they OPEN the Level Settings modal; concurrent admins see a read-only
 * "editing in progress" state. Saving pushes to S3 and every client picks it
 * up on its next load/refresh.
 *
 * Backward-compat: if the backend manifest endpoint is unreachable (e.g. during
 * rollout) the provider falls back to the legacy per-browser localStorage map
 * so categorization still works. Legacy localStorage is also used as a one-time
 * migration seed the first time an admin opens an empty manifest.
 */

import {
  createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode,
} from 'react';
import { BACKEND_URL } from '@/config';
import { useAuth } from '@/context/AuthContext';
import { getEffectiveRole } from '@/utils/roles';

export type LevelCategory = 'singleplayer' | 'labz' | 'ugc' | 'twod' | 'pvp' | null;

export interface LockInfo {
  lockId?: string;
  owner: string;
  acquiredAt: string;
  expiresAt: string;
}

interface LevelSettingsContextType {
  /** Explicitly-assigned categories from the saved manifest (manual only). */
  levelSettings: Record<string, LevelCategory>;
  /** Explicit manifest value for a level, or null if unassigned. */
  getLevelCategory: (levelId: string) => LevelCategory;
  /** Resolved category for display/charts: manifest -> name pattern -> UGC. */
  getEffectiveCategory: (levelId: string) => Exclude<LevelCategory, null>;

  manifestVersion: number;
  manifestUpdatedBy: string | null;
  manifestUpdatedAt: string | null;

  isAdmin: boolean;
  /** Current lock holder (null = free). */
  lock: LockInfo | null;
  /** True when this client holds the edit lock. */
  lockMine: boolean;
  acquiring: boolean;
  saving: boolean;
  error: string | null;

  /** Legacy localStorage map, used to seed a first-time migration. */
  legacyLocalSettings: Record<string, LevelCategory>;

  isSettingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  /** Persist a categories map (admin, requires held lock). Returns success. */
  saveManifest: (categories: Record<string, LevelCategory>) => Promise<boolean>;
  refresh: () => Promise<boolean>;
}

const LevelSettingsContext = createContext<LevelSettingsContextType | null>(null);

const LEGACY_STORAGE_KEY = 'lokko_level_settings';
const API = `${BACKEND_URL}/api/v1/level-settings`;
// Heartbeat well under the backend's 10-minute lock TTL.
const HEARTBEAT_MS = 120_000;
const VALID: ReadonlySet<string> = new Set(['singleplayer', 'labz', 'ugc', 'twod', 'pvp']);

// --- Built-in default name patterns (Single Player / Labz auto-grouping) -----
const DEFAULT_SETTINGS: Record<string, Exclude<LevelCategory, null>> = {
  jungle: 'singleplayer', nightcity: 'singleplayer', lava: 'singleplayer',
  monastery: 'singleplayer', monastry: 'singleplayer', gravgun: 'singleplayer',
  icebergs: 'singleplayer', supertraversal: 'singleplayer', super_traversal: 'singleplayer',
  supertraverasl: 'singleplayer', 'desert eagle': 'singleplayer', desert_eagle: 'singleplayer',
  deserteagle: 'singleplayer',
  superpunch: 'labz', heaven: 'labz', cubegun: 'labz', hell: 'labz',
};

// Single Player overrides — win even over a labs_/labz_ prefix.
const SINGLEPLAYER_OVERRIDE_PATTERNS = [
  'supertraversal', 'super_traversal', 'supertraverasl', 'icebergs',
  'desert eagle', 'desert_eagle', 'deserteagle',
];

/**
 * Name-pattern category for a level, or null if no pattern matches.
 * PVP and 2D are intentionally NOT auto-detected (product decision: manual
 * category only), so they only ever come from an explicit manifest assignment.
 */
export function getDefaultCategory(levelId: string): Exclude<LevelCategory, null> | null {
  const lower = levelId.toLowerCase();
  for (const pattern of SINGLEPLAYER_OVERRIDE_PATTERNS) {
    if (lower.includes(pattern)) return 'singleplayer';
  }
  if (lower.startsWith('labs_') || lower.startsWith('labs ') || lower.startsWith('labz_') || lower.startsWith('labz ')) {
    return 'labz';
  }
  for (const [pattern, category] of Object.entries(DEFAULT_SETTINGS)) {
    if (lower.includes(pattern)) return category;
  }
  return null;
}

function readLegacyLocalSettings(): Record<string, LevelCategory> {
  try {
    const stored = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored) as Record<string, unknown>;
    const out: Record<string, LevelCategory> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string' && VALID.has(v)) out[k] = v as LevelCategory;
    }
    return out;
  } catch {
    return {};
  }
}

export function LevelSettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const username = user?.email || user?.username || 'unknown';
  const isAdmin = getEffectiveRole(user?.groups) === 'admin';

  const [levelSettings, setLevelSettings] = useState<Record<string, LevelCategory>>({});
  const [manifestVersion, setManifestVersion] = useState(0);
  const [manifestUpdatedBy, setManifestUpdatedBy] = useState<string | null>(null);
  const [manifestUpdatedAt, setManifestUpdatedAt] = useState<string | null>(null);

  const [lock, setLock] = useState<LockInfo | null>(null);
  const [lockMine, setLockMine] = useState(false);
  const [acquiring, setAcquiring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const legacyRef = useRef<Record<string, LevelCategory>>(readLegacyLocalSettings());
  const lockIdRef = useRef<string | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const applyManifest = useCallback((manifest: any) => {
    if (!manifest || typeof manifest !== 'object') return;
    const cats = (manifest.categories && typeof manifest.categories === 'object') ? manifest.categories : {};
    const clean: Record<string, LevelCategory> = {};
    for (const [k, v] of Object.entries(cats)) {
      if (typeof v === 'string' && VALID.has(v)) clean[k] = v as LevelCategory;
    }
    setLevelSettings(clean);
    setManifestVersion(Number(manifest.version) || 0);
    setManifestUpdatedBy(manifest.updatedBy ?? null);
    setManifestUpdatedAt(manifest.updatedAt ?? null);
  }, []);

  // Returns true if the manifest was loaded from the backend, false if we fell
  // back to legacy localStorage. Editing is only permitted when this is true,
  // so a Save can never overwrite the shared manifest with stale local state.
  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch(API);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      applyManifest(data.manifest);
      setLock(data.lock ?? null);
      return true;
    } catch (err) {
      // Backend unreachable -> fall back to legacy localStorage so the
      // dashboard still categorizes during rollout (read-only).
      console.warn('Level settings: backend unavailable, using legacy localStorage', err);
      if (Object.keys(legacyRef.current).length) setLevelSettings(legacyRef.current);
      return false;
    }
  }, [applyManifest]);

  // Initial load
  useEffect(() => { void refresh(); }, [refresh]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(async () => {
      const lid = lockIdRef.current;
      if (!lid) return;
      try {
        const res = await fetch(`${API}/lock/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lockId: lid }),
        });
        if (!res.ok) {
          // Only a 409 means the lock was genuinely lost (expired / taken
          // over). A 5xx / proxy error (ALB 502/504) is transient -- keep the
          // lock and let the next tick retry, so a blip doesn't strand a valid
          // server-side lock for the rest of its TTL.
          if (res.status === 409) {
            setLockMine(false);
            lockIdRef.current = null;
            stopHeartbeat();
            setError('Edit lock was lost. Reopen to continue editing.');
            const data = await res.json().catch(() => null);
            if (data?.lock) setLock(data.lock);
          }
        }
      } catch {
        /* transient network error -- next tick retries */
      }
    }, HEARTBEAT_MS);
  }, [stopHeartbeat]);

  const openSettings = useCallback(async () => {
    setIsSettingsOpen(true);
    setError(null);
    const loaded = await refresh();

    if (!isAdmin) return; // non-admins get a read-only view

    if (!loaded) {
      // Manifest couldn't be read — refuse to acquire the lock so a Save can't
      // overwrite the shared server manifest with stale local/legacy data.
      setError('Could not load level settings from the server. Editing is disabled to protect the shared manifest — please retry.');
      return;
    }

    setAcquiring(true);
    try {
      const res = await fetch(`${API}/lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.lock) {
        lockIdRef.current = data.lock.lockId ?? null;
        setLock(data.lock);
        setLockMine(true);
        startHeartbeat();
      } else {
        // 409 -> someone else holds it.
        setLockMine(false);
        setLock(data.lock ?? null);
      }
    } catch (err) {
      setLockMine(false);
      setError('Could not reach the server to acquire the edit lock.');
    } finally {
      setAcquiring(false);
    }
  }, [isAdmin, username, refresh, startHeartbeat]);

  const closeSettings = useCallback(async () => {
    stopHeartbeat();
    const lid = lockIdRef.current;
    setIsSettingsOpen(false);
    if (lid) {
      lockIdRef.current = null;
      setLockMine(false);
      try {
        await fetch(`${API}/lock/release`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lockId: lid }),
        });
      } catch { /* best-effort; lock will TTL-expire anyway */ }
    }
    void refresh();
  }, [stopHeartbeat, refresh]);

  const saveManifest = useCallback(async (categories: Record<string, LevelCategory>): Promise<boolean> => {
    const lid = lockIdRef.current;
    if (!lid) { setError('No active edit lock.'); return false; }
    // Build a minimal payload: keep only valid categories, and drop any entry
    // that already equals the level's resolved default (name pattern, else
    // 'ugc'). Omitting a default-equal entry yields the identical effective
    // category, so this keeps the shared manifest small even though the modal
    // now always writes an explicit draft entry on click.
    const payload: Record<string, string> = {};
    for (const [k, v] of Object.entries(categories)) {
      if (typeof v !== 'string' || !VALID.has(v)) continue;
      const def = getDefaultCategory(k) ?? 'ugc';
      if (v === def) continue;
      payload[k] = v;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(API, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        // baseVersion enables optimistic-concurrency on the server: if someone
        // else saved since we loaded, our stale-based write is rejected instead
        // of silently overwriting theirs.
        body: JSON.stringify({ categories: payload, lockId: lid, username, baseVersion: manifestVersion }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.manifest) {
        applyManifest(data.manifest);
        // One-time migration done -> stop relying on legacy localStorage.
        try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* ignore */ }
        legacyRef.current = {};
        return true;
      }
      if (data?.reason === 'stale') {
        // Someone saved between our load and this Save. Load the latest and END
        // the edit session (release our lock -> the modal goes read-only) so the
        // stale draft cannot simply be re-submitted on a second click and
        // clobber the newer save. The admin must reopen, which re-seeds the
        // draft from the latest manifest. (In the common case where this was
        // our own save whose HTTP response was lost, reopening shows our changes
        // already persisted.)
        if (data.manifest) applyManifest(data.manifest);
        lockIdRef.current = null;
        setLockMine(false);
        stopHeartbeat();
        if (lid) {
          fetch(`${API}/lock/release`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lockId: lid }),
          }).catch(() => { /* best-effort; TTL will free it */ });
        }
        setError('Level settings changed on the server since you opened them. The latest has been loaded — reopen Level Settings to make further changes.');
      } else if (res.status === 409) {
        setLockMine(false);
        lockIdRef.current = null;
        stopHeartbeat();
        setError('Edit lock was lost; your changes were not saved. Reopen to retry.');
        if (data?.lock) setLock(data.lock);
      } else {
        setError('Failed to save level settings.');
      }
      return false;
    } catch {
      setError('Network error while saving level settings.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [username, manifestVersion, applyManifest, stopHeartbeat]);

  // Release the lock if the tab unloads mid-edit.
  useEffect(() => {
    const handler = () => {
      const lid = lockIdRef.current;
      if (lid && navigator.sendBeacon) {
        try {
          navigator.sendBeacon(
            `${API}/lock/release`,
            new Blob([JSON.stringify({ lockId: lid })], { type: 'application/json' }),
          );
        } catch { /* ignore */ }
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      stopHeartbeat();
    };
  }, [stopHeartbeat]);

  const getLevelCategory = useCallback((levelId: string): LevelCategory => {
    return levelSettings[levelId] ?? null;
  }, [levelSettings]);

  const getEffectiveCategory = useCallback((levelId: string): Exclude<LevelCategory, null> => {
    const explicit = levelSettings[levelId];
    if (explicit) return explicit;
    return getDefaultCategory(levelId) ?? 'ugc';
  }, [levelSettings]);

  return (
    <LevelSettingsContext.Provider value={{
      levelSettings,
      getLevelCategory,
      getEffectiveCategory,
      manifestVersion,
      manifestUpdatedBy,
      manifestUpdatedAt,
      isAdmin,
      lock,
      lockMine,
      acquiring,
      saving,
      error,
      legacyLocalSettings: legacyRef.current,
      isSettingsOpen,
      openSettings,
      closeSettings,
      saveManifest,
      refresh,
    }}>
      {children}
    </LevelSettingsContext.Provider>
  );
}

export function useLevelSettings() {
  const context = useContext(LevelSettingsContext);
  if (!context) {
    throw new Error('useLevelSettings must be used within LevelSettingsProvider');
  }
  return context;
}
